// Stage management constants and utilities
import { isValidAdminCharge } from "../constants/adminCharge";
import { hasRichTextContent } from "./richTextUtils";

export const MAX_STAGE = 9;
export const MIN_PACKAGE_IMAGES = 5;
export const EVENT_MAX_STAGE = 7;
export const ATTRACTION_MAX_STAGE = 6;

// Package stages
export const STAGES = {
  GENERAL: 1,
  ITINERARY: 2,
  STAY: 3,
  INCLUSION: 4,
  PRICING: 5,
  ADDITIONAL: 6,
  TERMS: 7,
  CANCELLATION: 8,
  FAQ: 9
};

// Event stages
export const EVENT_STAGES = {
  GENERAL: 1,
  TICKETS: 2,
  PRICING: 3,
  HIGHLIGHTS: 4,
  FAQ: 5,
  TERMS: 6,
  CANCELLATION: 7
};

// Attraction stages
export const ATTRACTION_STAGES = {
  GENERAL: 1,
  TICKETS: 2,
  PRICING: 3,
  INCLUSIONS: 4,
  FAQ: 5,
  TERMS: 6
};

// Package stage IDs
export const STAGE_IDS = {
  1: 'general',
  2: 'itinerary', 
  3: 'stay',
  4: 'inclusion',
  5: 'pricing',
  6: 'additional',
  7: 'terms',
  8: 'cancellation',
  9: 'faq'
};

// Event stage IDs
export const EVENT_STAGE_IDS = {
  1: 'general',
  2: 'tickets',
  3: 'pricing',
  4: 'highlights',
  5: 'faq',
  6: 'terms',
  7: 'cancellation'
};

// Attraction stage IDs
export const ATTRACTION_STAGE_IDS = {
  1: 'general',
  2: 'tickets',
  3: 'pricing',
  4: 'inclusions',
  5: 'faq',
  6: 'terms'
};

// Stage validation rules for packages
export const isItineraryDaySaveable = (day) => {
  const destination = String(day?.destination || "").trim();
  return Boolean(destination && hasRichTextContent(day?.description));
};

export const stageValidationRules = {
  [STAGES.GENERAL]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [STAGES.ITINERARY]: (data) => {
    if (!Array.isArray(data?.days) || data.days.length === 0) return false;
    return data.days.some(
      (day) => Number(day.is_completed) === 1 || isItineraryDaySaveable(day)
    );
  },
  [STAGES.STAY]: (data) => {
    return data?.hasValidStays;
  },
  [STAGES.INCLUSION]: (data) => {
    return (
      (data?.detailed_inclusions?.length > 0) ||
      (data?.exclusions?.length > 0) ||
      (data?.things_to_carry?.length > 0)
    );
  },
  [STAGES.PRICING]: (data) => {
    if (!data?.payment_terms || Object.keys(data.payment_terms).length === 0) {
      return false;
    }
    if (!data.categories?.length) return false;

    const categoryHasBasePrice = (category) => {
      const bp = category.base_price;
      if (!bp) return Boolean(category.is_base_price_set);

      const adultAdminFee = bp.adult?.adminFee;
      const childAdminFee = bp.child?.adminFee || adultAdminFee;

      return (
        bp.adult?.basePrice &&
        adultAdminFee &&
        bp.child?.basePrice &&
        childAdminFee
      );
    };

    return data.categories.every(categoryHasBasePrice);
  },
  [STAGES.ADDITIONAL]: (data) => {
    return data?.additional_info?.trim().length > 0;
  },
  [STAGES.TERMS]: (data) => {
    return data?.terms_and_conditions?.trim().length > 0;
  },
  [STAGES.CANCELLATION]: (data) => {
    if (!data?.data?.length) return false;
    return data.data.some(
      (rule) =>
        rule.cancellation_days ||
        rule.cancellation_days_from ||
        rule.refund_percentage
    );
  },
  [STAGES.FAQ]: (data) => {
    return data && Object.keys(data).length > 0;
  }
};

// Stage validation rules for events
export const eventStageValidationRules = {
  [EVENT_STAGES.GENERAL]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.PRICING]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.HIGHLIGHTS]: (data) => {
    return data?.event_highlights?.length > 0;
  },
  [EVENT_STAGES.FAQ]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.TERMS]: (data) => {
    return data && Object.keys(data).length > 0;
  },
  [EVENT_STAGES.CANCELLATION]: (data) => {
    return data && Object.keys(data).length > 0;
  }
};

// Package stage helpers
export const getNextStage = (currentStage, isEvent = false) => {
  return Math.min(currentStage + 1, isEvent ? EVENT_MAX_STAGE : MAX_STAGE);
};

export const isStageAccessible = (targetStage, currentStage, completedStage) => {
  return targetStage <= currentStage || targetStage <= completedStage;
};

export const validateStage = (stage, data, isEvent = false) => {
  const validationRule = isEvent ? eventStageValidationRules[stage] : stageValidationRules[stage];
  return validationRule ? validationRule(data) : true;
};

// Package stage data getter
export const getStageData = (stage, {
  generalInfoData,
  itineraryData,
  stayData,
  inclusionData,
  pricingData,
  additionalInfoData,
  termsConditionData,
  cancellationData,
  faqData
}) => {
  const stageDataMap = {
    [STAGES.GENERAL]: generalInfoData,
    [STAGES.ITINERARY]: itineraryData,
    [STAGES.STAY]: stayData,
    [STAGES.INCLUSION]: inclusionData,
    [STAGES.PRICING]: pricingData,
    [STAGES.ADDITIONAL]: additionalInfoData,
    [STAGES.TERMS]: termsConditionData,
    [STAGES.CANCELLATION]: cancellationData,
    [STAGES.FAQ]: faqData
  };
  
  return stageDataMap[stage];
};

// Event stage data getter
export const getEventStageData = (stage, {
  generalInfoData,
  pricingData,
  highlightsData,
  termsConditionData,
  faqData,
  cancellationData
}) => {
  const stageDataMap = {
    [EVENT_STAGES.GENERAL]: generalInfoData,
    [EVENT_STAGES.TICKETS]: generalInfoData,
    [EVENT_STAGES.PRICING]: pricingData,
    [EVENT_STAGES.HIGHLIGHTS]: highlightsData,
    [EVENT_STAGES.FAQ]: faqData,
    [EVENT_STAGES.TERMS]: termsConditionData,
    [EVENT_STAGES.CANCELLATION]: cancellationData
  };
  
  return stageDataMap[stage];
};

export const isEventFreeBooking = (generalInfoData) =>
  Boolean(generalInfoData?.free_booking);

export const getEventNextStage = (currentStage, isFreeBooking = false) => {
  let next = Math.min(Number(currentStage) + 1, EVENT_MAX_STAGE);
  if (isFreeBooking && next === EVENT_STAGES.PRICING) {
    next = EVENT_STAGES.HIGHLIGHTS;
  }
  return next;
};

export const getEffectiveEventCompletedStage = (savedStage, isFreeBooking = false) => {
  const stage = Number(savedStage) || 1;
  if (isFreeBooking && stage >= EVENT_STAGES.PRICING) {
    return Math.max(stage, EVENT_STAGES.HIGHLIGHTS);
  }
  return stage;
};

//*******************************************************************************
// ATTRACTION STAGE MANAGEMENT
//*******************************************************************************

// Attraction stage validation rules
export const attractionStageValidationRules = {
  [ATTRACTION_STAGES.GENERAL]: (data) => {
    return data &&
           data.name &&
           data.name.trim().length > 0 &&
           data.category &&
           data.chargable_from !== "" &&
           data.chargable_from !== null &&
           data.chargable_from !== undefined &&
           hasRichTextContent(data.description) &&
           data.location?.address &&
           data.location.address.trim().length > 0 &&
           data.latitude &&
           data.longitude &&
           String(data.start_time ?? "").trim() &&
           String(data.end_time ?? "").trim() &&
           String(data.address ?? data.location?.address ?? "").trim();
  },
  [ATTRACTION_STAGES.TICKETS]: (data) => {
    const tickets = data?.tickets ?? data?.data ?? data;
    if (!Array.isArray(tickets) || tickets.length === 0) return false;
    return tickets.some((ticket) => String(ticket?.name ?? "").trim());
  },
  [ATTRACTION_STAGES.PRICING]: (data) => {
    if (!data || !data.regularTickets || !Array.isArray(data.regularTickets)) {
      return false;
    }

    const isPaxMode = data.pricingMode === "pax";

    return data.regularTickets.some(ticket =>
      ticket.price > 0 &&
      isValidAdminCharge(ticket.adminCharge) &&
      hasRichTextContent(ticket.description) &&
      ticket.maximumAllowedBookingsPerUser > 0 &&
      (!isPaxMode || (ticket.childPrice !== "" && ticket.childPrice !== null && ticket.childPrice !== undefined && Number(ticket.childPrice) >= 0))
    );
  },
  [ATTRACTION_STAGES.FAQ]: (data) => {
    if (!data?.faqs?.length) return false;
    return data.faqs.some(
      (faq) =>
        String(faq?.question ?? "").trim() &&
        String(faq?.answer ?? "").trim()
    );
  },
  [ATTRACTION_STAGES.INCLUSIONS]: () => true,
  [ATTRACTION_STAGES.TERMS]: (data) => {
    return hasRichTextContent(data?.description);
  }
};

export const isAttractionFreeBooking = (generalInfoData) =>
  Boolean(generalInfoData?.free_booking);

export const getAttractionNextStage = (currentStage, isFreeBooking = false) => {
  let next = Math.min(Number(currentStage) + 1, ATTRACTION_MAX_STAGE);
  if (isFreeBooking && next === ATTRACTION_STAGES.PRICING) {
    next = ATTRACTION_STAGES.INCLUSIONS;
  }
  return next;
};

export const getEffectiveAttractionCompletedStage = (
  savedStage,
  isFreeBooking = false
) => {
  const stage = Number(savedStage) || 1;
  if (isFreeBooking && stage >= ATTRACTION_STAGES.PRICING) {
    return Math.max(stage, ATTRACTION_STAGES.INCLUSIONS);
  }
  return stage;
};

export const isAttractionStageAccessible = (targetStage, currentStage, completedStage) => {
  const highestAccessibleStage = completedStage || 1;
  return targetStage <= highestAccessibleStage;
};

// Validate attraction stage
export const validateAttractionStage = (stage, data) => {
  const validationRule = attractionStageValidationRules[stage];
  return validationRule ? validationRule(data) : true;
};

// Get attraction stage data
export const getAttractionStageData = (stage, {
  generalInfoData,
  pricingData,
  faqData,
  inclusionExclusionData,
  termsConditionData,
  ticketsData,
}) => {
  const stageDataMap = {
    [ATTRACTION_STAGES.GENERAL]: generalInfoData,
    [ATTRACTION_STAGES.TICKETS]: { tickets: ticketsData || [] },
    [ATTRACTION_STAGES.PRICING]: pricingData,
    [ATTRACTION_STAGES.FAQ]: faqData,
    [ATTRACTION_STAGES.INCLUSIONS]: inclusionExclusionData,
    [ATTRACTION_STAGES.TERMS]: termsConditionData,
  };

  return stageDataMap[stage];
}; 